import { useEffect, useRef } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://oopbuyspreadsheets.lol';

const HOME_DEFAULTS = {
  title: 'OOPBuy Spreadsheet – The Complete Tracking & Automation System Guide',
  description:
    'OOPBuy Spreadsheet is the ultimate order tracking and ecommerce automation system. Discover complete guides, category comparisons, spreadsheet tracking tips, and SEO-optimized resources for smart sellers managing orders efficiently with the oopbuy spreadsheet system.',
  keywords:
    'oopbuy spreadsheet, spreadsheet tracking system, order management spreadsheet, ecommerce spreadsheet automation, oopbuy spreadsheet guide',
  canonical: `${SITE_URL}/`,
  ogTitle: 'OOPBuy Spreadsheet – The Complete Tracking & Automation System Guide',
  ogDescription:
    'Discover the ultimate oopbuy spreadsheet system for ecommerce order tracking and automation. Guides, comparisons, and resources for smart sellers.',
  ogUrl: `${SITE_URL}/`,
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterTitle: 'OOPBuy Spreadsheet – The Complete Tracking & Automation System Guide',
  twitterDescription:
    'Discover the ultimate oopbuy spreadsheet system for ecommerce order tracking and automation.',
};

interface PageSEOData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
}

interface PageSEOProps {
  data: PageSEOData;
  jsonLd?: Record<string, unknown>[];
}

function setOrCreateMeta(attrName: string, attrValue: string, content: string) {
  let el: Element | null = document.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export default function PageSEO({ data, jsonLd }: PageSEOProps) {
  const cleanupRef = useRef<(() => void) | null>(null);
  const previousTitleRef = useRef<string>(document.title);

  useEffect(() => {
    // Save current title
    previousTitleRef.current = document.title;

    // Update document title
    document.title = data.title;

    // Update meta tags
    setOrCreateMeta('name', 'description', data.description);
    setOrCreateMeta('name', 'keywords', data.keywords);
    const linkCanonical = document.querySelector('link[rel="canonical"]');
    if (linkCanonical) {
      linkCanonical.setAttribute('href', data.canonical);
    } else {
      const el = document.createElement('link');
      el.setAttribute('rel', 'canonical');
      el.setAttribute('href', data.canonical);
      document.head.appendChild(el);
    }
    setOrCreateMeta('property', 'og:title', data.ogTitle || data.title);
    setOrCreateMeta('property', 'og:description', data.ogDescription || data.description);
    setOrCreateMeta('property', 'og:url', data.ogUrl || data.canonical);
    setOrCreateMeta('property', 'og:type', data.ogType || 'website');
    setOrCreateMeta('name', 'twitter:card', data.twitterCard || 'summary_large_image');
    setOrCreateMeta('name', 'twitter:title', data.twitterTitle || data.ogTitle || data.title);
    setOrCreateMeta(
      'name',
      'twitter:description',
      data.twitterDescription || data.ogDescription || data.description,
    );

    // Inject JSON-LD scripts
    const injectedScripts: HTMLScriptElement[] = [];
    if (jsonLd && jsonLd.length > 0) {
      // Remove any previously injected dynamic JSON-LD
      document.head.querySelectorAll('script[data-seo-dynamic="jsonld"]').forEach((s) => s.remove());

      jsonLd.forEach((item) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-dynamic', 'jsonld');
        script.textContent = JSON.stringify(item);
        document.head.appendChild(script);
        injectedScripts.push(script);
      });
    }

    // Store cleanup function
    cleanupRef.current = () => {
      document.title = previousTitleRef.current;
      injectedScripts.forEach((s) => s.remove());

      // Restore home defaults for meta tags (since home is the default)
      setOrCreateMeta('name', 'description', HOME_DEFAULTS.description);
      setOrCreateMeta('name', 'keywords', HOME_DEFAULTS.keywords);
      const canon = document.querySelector('link[rel="canonical"]');
      if (canon) canon.setAttribute('href', HOME_DEFAULTS.canonical);
      setOrCreateMeta('property', 'og:title', HOME_DEFAULTS.ogTitle);
      setOrCreateMeta('property', 'og:description', HOME_DEFAULTS.ogDescription);
      setOrCreateMeta('property', 'og:url', HOME_DEFAULTS.ogUrl);
      setOrCreateMeta('property', 'og:type', HOME_DEFAULTS.ogType);
    };

    return () => {
      if (cleanupRef.current) cleanupRef.current();
    };
  }, [data, jsonLd]);

  return null;
}

export { HOME_DEFAULTS, SITE_URL };